import { Latencies, ReportChartDataLegacy } from 'interfaces/performanceTest';
import dayjs from 'dayjs';
import { scaleTime } from 'd3-scale';
import { timeSecond } from 'd3-time';
import { reduce } from 'lodash';
import { ChartsByLabel } from 'interfaces/performanceTest';

const tickFormatter = (timeStamp: number): string => {
  return dayjs.unix(timeStamp).format('h:mm:ss a');
};

export const commonXAxisProps = {
  dataKey: 'timeStamp',
  tickFormatter: tickFormatter,
  // tickMargin: 15,
};

export const durationFormatter = (range: number[]): string => {
  if (!range[1]) {
    return 'Active';
  }

  const diff = Math.round((range[1] - range[0]) / 60);
  // TODO(bobsin): improve this to be dynamic based on diff size
  return `${diff} minute${diff == 1 ? '' : 's'}`;
};

export interface LabeledDataPoint<T> {
  metricByLabel: {
    [label: string]: T;
  };
  timeStamp: number;
}

interface DataPoint {
  timeStamp: number;
  [metric: string]: any;
}

export const buildResponseTimePlots = (
  chartsByLabel: ChartsByLabel
): LabeledDataPoint<Latencies>[] => {
  return Object.values(
    reduce(
      chartsByLabel,
      (memo, chart, label) => {
        chart.timeStamps.forEach((timeStamp, i) => {
          memo[timeStamp] = memo[timeStamp] || {
            timeStamp,
            metricByLabel: {},
          };

          memo[timeStamp].metricByLabel[label] = {
            avgMs: chart.latencyAvgMs[i],
            minMs: chart.latencyMinMs[i],
            maxMs: chart.latencyMaxMs[i],
            p50Ms: chart.latencyP50Ms[i],
            p75Ms: chart.latencyP75Ms[i],
            p90Ms: chart.latencyP90Ms[i],
            p95Ms: chart.latencyP95Ms[i],
            p99Ms: chart.latencyP99Ms[i],
          };
        });

        return memo;
      },
      []
    )
  );
};

export const buildVirtualUserPlots = (
  chart: ReportChartDataLegacy
): DataPoint[] => {
  const plots: DataPoint[] = [];

  chart.timeStamps.forEach((timeStamp, i) => {
    plots.push({
      timeStamp,
      virtualUsers: chart.virtualUsers[i],
    });
  });

  return plots;
};

export const reduceChartsByLabel = <T>(
  chartsByLabel: ChartsByLabel,
  path: string
): LabeledDataPoint<T>[] => {
  const groupedByTs = reduce(
    chartsByLabel,
    (memo, chart, label) => {
      chart.timeStamps.forEach((timeStamp, i) => {
        memo[timeStamp] = memo[timeStamp] || {
          timeStamp,
          metricByLabel: {},
        };
        memo[timeStamp].metricByLabel[label] = chart[path][i];
      });

      return memo;
    },
    []
  );

  return Object.values(groupedByTs);
};

export const fillInTimeGaps = <T extends DataPoint>(
  rows: DataPoint[],
  emptyRow: (timeStamp: number) => T,
  mergeRow: (timeStamp: number, existing: T) => T
): T[] => {
  if (rows.length === 0) {
    return [];
  }

  const startTime = dayjs.unix(rows[0].timeStamp).toDate();
  const endTime = dayjs.unix(rows[rows.length - 1].timeStamp).toDate();
  let currentIdx = 0;

  return scaleTime()
    .domain([startTime, endTime])
    .ticks(timeSecond.every(5))
    .map((date) => {
      const unix = date.getTime() / 1000;
      const current = rows[currentIdx];

      if (unix < current.timeStamp) {
        return emptyRow(unix);
      } else if (unix === current.timeStamp) {
        currentIdx += 1;
        return mergeRow(unix, current as T);
      }
    });
};
